import DOMPurify from 'dompurify';
import * as htmlToImage from 'html-to-image';
import type Konva from 'konva';
import { useEffect, useRef, useState } from 'react';
import {
  Circle,
  Ellipse,
  Group,
  Image as KonvaImage,
  Rect,
  RegularPolygon,
  Star,
  Transformer,
} from 'react-konva';

import { DimensionLabel } from './DimensionLabel';
import { type ContrastPadding, type TextElement, useTextEditorStore } from './useTextEditorStore';

import { loadGoogleFont } from '@/lib/fonts/load-google-font';
import { logger } from '@/utils/logger';

/**
 * Allowlisted tags + attributes for text-overlay HTML. The current TipTap
 * config (NativeTextarea.tsx) only emits this minimal set, so this allowlist
 * is a defense-in-depth wall — not a behavior change. It exists so a future
 * config drift (someone adds Link or Image extensions) or a malformed
 * overlay_layers payload (direct DB write, template import) cannot escalate
 * into XSS via the innerHTML sink below.
 *
 * The project ships with `'unsafe-inline'` and `'unsafe-eval'` in
 * `script-src` (vercel.json), so CSP would NOT block an injected
 * `<img onerror>` or `<iframe srcdoc>`. DOMPurify is the only sanitizer.
 */
const ALLOWED_TEXT_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'code',
  'pre', // CodeBlock extension wraps code in <pre><code> — without this,
  // pasted code blocks render as inline code only (visual regression).
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'hr',
  'span',
];

/** Compute the contrast padding for a given mode and intensity.
 *  Exported so TextOverlayToolbar can store it on the element. */
export function computeContrastPadding(
  mode: TextElement['contrastMode'],
  intensity: number,
): ContrastPadding {
  let pad = 0;
  switch (mode) {
    case 'glow':
      pad = Math.ceil((intensity / 100) * 40) + 4;
      break;
    case 'drop-shadow':
      pad = Math.ceil((intensity / 100) * 8) + 4;
      break;
    case 'outline':
      pad = Math.ceil((intensity / 100) * 6) + 2;
      break;
    case 'bg-box':
      pad = 0; // Background handled by Konva Rect, not rasterized PNG padding
      break;
    default:
      pad = 0;
  }
  return { top: pad, right: pad, bottom: pad, left: pad };
}

interface KonvaTextNodeProps {
  element: TextElement;
  isSelected: boolean;
  isEditing: boolean;
  onDoubleClick: () => void;
  onDragMove?: (
    id: string,
    x: number,
    y: number,
    w: number,
    h: number,
    node: { x: (v: number) => void; y: (v: number) => void },
  ) => void;
  onDragEndGuides?: () => void;
}

export function KonvaTextNode({
  element,
  isSelected,
  isEditing,
  onDoubleClick,
  onDragMove,
  onDragEndGuides,
}: KonvaTextNodeProps) {
  const shapeRef = useRef<Konva.Group>(null);
  const trRef = useRef<Konva.Transformer>(null);
  const [renderedImage, setRenderedImage] = useState<HTMLImageElement | null>(null);
  // Track the fontSize that the current renderedImage was built from,
  // so we can apply a temporary scale during the rasterization gap.
  const renderedFontSizeRef = useRef<number>(element.fontSize);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDims, setResizeDims] = useState({ w: 0, h: 0 });
  const initialTransformRef = useRef<{ width: number; height: number; fontSize: number } | null>(
    null,
  );

  const updateElement = useTextEditorStore((state) => state.updateElement);
  const selectElement = useTextEditorStore((state) => state.selectElement);

  // Ensure the element's font is actually loaded before/at rasterization — this is
  // the single chokepoint covering ALL paths that set a font (picker, brand
  // default on creation, template import, restored overlay_layers). Without it,
  // unloaded picker fonts (19 of 24) rasterized as the system fallback. Bumping
  // fontTick re-runs the raster effect once the real glyphs are available.
  const [fontTick, setFontTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void loadGoogleFont(element.fontFamily).then(() => {
      if (!cancelled) setFontTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [element.fontFamily]);

  // When not editing, generate a base64 image from the HTML content
  useEffect(() => {
    if (isEditing) return;

    const { incrementRasterPending, decrementRasterPending } = useTextEditorStore.getState();
    incrementRasterPending();

    // Track whether the async rasterization pipeline has started. If cleanup
    // fires before the timeout, we cancel the timeout and decrement ourselves.
    // If cleanup fires AFTER toPng is in-flight, we set `cancelled` so the
    // callback's finally-block handles the decrement instead — preventing the
    // double-decrement race that let handleSave export a stale canvas.
    let toPngStarted = false;
    let cancelled = false;

    // Create a temporary hidden DOM element to render the HTML
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    container.style.width = `${element.width}px`;
    // Widen container to include contrast padding so textShadow isn't clipped
    const cp = element.contrastPadding;
    const hPad = cp.left + cp.right;
    if (hPad > 0 && element.contrastMode !== 'bg-box') {
      container.style.width = `${element.width + hPad}px`;
    }
    container.style.fontFamily = element.fontFamily;
    container.style.fontSize = `${element.fontSize}px`;
    container.style.lineHeight = `${element.lineHeight ?? 1.2}`;
    container.style.pointerEvents = 'none';

    // Vertical alignment: use flexbox to position content within container.
    // Only works when the user has set an explicit height via bounding box resize —
    // otherwise the container auto-sizes to content and centering is a no-op.
    // V-Align offset is computed at render time from the rasterized image height
    // and applied as a KonvaImage y-offset — no DOM flex positioning needed here.

    const content = document.createElement('div');
    content.style.width = `${element.width}px`;
    if (hPad > 0 && element.contrastMode !== 'bg-box') {
      content.style.width = `${element.width + hPad}px`;
    }
    content.style.fontFamily = element.fontFamily;
    content.style.fontSize = `${element.fontSize}px`;
    content.style.fontWeight = String(element.fontWeight ?? 400);
    content.style.fontStyle = element.isItalic ? 'italic' : 'normal';
    content.style.textDecoration = element.isUnderline ? 'underline' : 'none';
    content.style.lineHeight = `${element.lineHeight ?? 1.2}`;
    content.style.letterSpacing = `${element.letterSpacing ?? 0}em`;
    content.style.textAlign = element.textAlign ?? 'left';
    content.style.textTransform = element.textTransform ?? 'none';
    if (typeof element.fill === 'string') {
      content.style.color = element.fill;
    } else {
      // Gradient text fill via CSS
      const { colors, angle } = element.fill;
      content.style.backgroundImage = `linear-gradient(${angle}deg, ${colors.join(', ')})`;
      content.style.webkitBackgroundClip = 'text';
      content.style.webkitTextFillColor = 'transparent';
      content.style.backgroundClip = 'text';
    }
    if (cp.top > 0 || cp.right > 0 || cp.bottom > 0 || cp.left > 0) {
      content.style.padding = `${cp.top}px ${cp.right}px ${cp.bottom}px ${cp.left}px`;
    } else {
      content.style.padding = '0px';
    }

    if (element.contrastMode === 'drop-shadow') {
      content.style.textShadow = `0px ${(element.contrastIntensity / 100) * 8}px ${(element.contrastIntensity / 100) * 8}px ${element.contrastColor}`;
    } else if (element.contrastMode === 'glow') {
      content.style.textShadow = `0px 0px ${(element.contrastIntensity / 100) * 40}px ${element.contrastColor}`;
    } else if (element.contrastMode === 'outline') {
      content.style.webkitTextStroke = `${(element.contrastIntensity / 100) * 6}px ${element.contrastColor}`;
    }

    content.className = 'whitespace-pre-wrap break-words m-0';
    // Sanitize via DOMPurify before innerHTML assignment. Even though the
    // current TipTap config emits only safe tags, the persisted text comes
    // from a JSONB column — a future template-import path, AI-generated
    // overlay, or direct DB write could inject `<img onerror>` / `<script>`.
    // The page's CSP includes `'unsafe-inline'` so DOMPurify is the only
    // wall. Allowlist matches TipTap's StarterKit output exactly (no <a>,
    // no <img>, no event handlers).
    const rawHtml =
      element.text === 'Double click to edit' ? '<p>Double click to edit</p>' : element.text;
    content.innerHTML = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ALLOWED_TEXT_TAGS,
      ALLOWED_ATTR: ['style'],
    });

    container.appendChild(content);
    document.body.appendChild(container);

    // Use a slight timeout to ensure the DOM has updated and fonts are loaded
    const timeout = setTimeout(() => {
      toPngStarted = true;
      htmlToImage
        .toPng(content, {
          pixelRatio: 2,
          skipAutoScale: true,
          // Font loading is automatically handled by html-to-image via dataURIs
        })
        .then((dataUrl) => {
          const img = new window.Image();
          img.src = dataUrl;
          img.onload = () => {
            if (!cancelled) {
              renderedFontSizeRef.current = element.fontSize;
              setRenderedImage(img);
            }
            // Always decrement — cleanup only decrements when toPng never started,
            // so when toPng DID start the async callback is solely responsible.
            decrementRasterPending();
          };
          img.onerror = () => {
            decrementRasterPending();
          };
        })
        .catch((err) => {
          logger.error('KonvaTextNode: failed to rasterize text element', err);
          decrementRasterPending();
        })
        .finally(() => {
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        });
    }, 50);

    return () => {
      clearTimeout(timeout);
      cancelled = true;
      // Only decrement if toPng never started — otherwise the async callback
      // chain is responsible for the decrement (and will bail via `cancelled`).
      if (!toPngStarted) {
        decrementRasterPending();
      }
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
    // contrastPadding is intentionally split into four primitive deps below
    // (.top/.right/.bottom/.left) instead of including the object directly.
    // computeContrastPadding returns a new object reference on every store
    // update even when values are identical — including the object would
    // trigger a rasterization storm. The exhaustive-deps warning is a known
    // false positive against this intentional optimization.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fontTick, // re-rasterize once the (possibly lazy-loaded) font is ready
    element.text,
    element.width,
    element.height,
    element.fontSize,
    element.fontFamily,
    element.fontWeight,
    element.isUnderline,
    element.isItalic,
    element.fill,
    element.lineHeight,
    element.letterSpacing,
    element.textAlign,
    element.textTransform,
    element.contrastMode,
    element.contrastColor,
    element.contrastIntensity,
    element.contrastPadding.top,
    element.contrastPadding.right,
    element.contrastPadding.bottom,
    element.contrastPadding.left,
    element.verticalAlign,
    isEditing,
  ]);

  // Compute expected height for placeholder and Group sizing.
  // If the user has explicitly set a height via bounding box resize, use that.
  // Otherwise auto-derive from rendered content (or fontSize fallback).
  const contentHeight = renderedImage
    ? renderedImage.height / 2
    : element.fontSize * (element.lineHeight ?? 1.2);
  const expectedHeight = element.height ?? contentHeight;

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.forceUpdate();
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, renderedImage, element.width, expectedHeight]);

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    updateElement(element.id, {
      x: e.target.x(),
      y: e.target.y(),
    });
    onDragEndGuides?.();
  };

  // Clamp drag to stay within the parent Stage bounds — prevents the user from
  // dragging the text element completely off the visible canvas (lost element
  // bug). Uses the stage's intrinsic size, not the element's bbox, since
  // rotation makes bbox math messy. Per Konva drag-bounds docs.
  const dragBoundFunc = (pos: { x: number; y: number }) => {
    const stage = shapeRef.current?.getStage();
    if (!stage) return pos;
    const stageW = stage.width();
    const stageH = stage.height();
    const bbox = shapeRef.current?.getClientRect({ skipTransform: true });
    const w = bbox?.width ?? 0;
    const h = bbox?.height ?? 0;
    return {
      x: Math.max(0, Math.min(pos.x, stageW - w)),
      y: Math.max(0, Math.min(pos.y, stageH - h)),
    };
  };

  const handleTransform = () => {
    const node = shapeRef.current;
    if (!node) return;

    // Capture initial dims on first frame of the transform
    if (!initialTransformRef.current) {
      initialTransformRef.current = {
        width: element.width,
        height: renderedImage ? renderedImage.height / 2 : element.fontSize,
        fontSize: element.fontSize,
      };
    }

    const sx = node.scaleX();
    const sy = node.scaleY();

    // Normalize scale immediately — converts scale to width/height changes
    // This prevents the rasterized PNG from visually stretching during drag
    const newW = Math.max(30, node.width() * sx);
    const newH = Math.max(10, node.height() * sy);
    node.width(newW);
    node.height(newH);
    node.scaleX(1);
    node.scaleY(1);

    setIsResizing(true);
    setResizeDims({ w: newW, h: newH });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    if (!node) return;

    const initial = initialTransformRef.current;
    const finalW = node.width();
    const finalH = node.height();

    const updates: Partial<TextElement> = {
      x: node.x(),
      y: node.y(),
      width: finalW,
      height: finalH,
      rotation: node.rotation(),
      scaleX: 1,
      scaleY: 1,
    };

    // Corner resize: both dimensions changed significantly → scale fontSize
    if (initial) {
      const wRatio = finalW / initial.width;
      const hRatio = finalH / initial.height;
      const isCorner = Math.abs(wRatio - 1) > 0.05 && Math.abs(hRatio - 1) > 0.05;
      if (isCorner) {
        updates.fontSize = Math.max(8, initial.fontSize * hRatio);
      }
    }

    updateElement(element.id, updates);
    initialTransformRef.current = null;
    setIsResizing(false);
  };

  return (
    <>
      <Group
        ref={shapeRef}
        x={element.x}
        y={element.y}
        width={isResizing ? resizeDims.w : element.width}
        height={isResizing ? resizeDims.h : expectedHeight}
        rotation={element.rotation}
        draggable={!isEditing}
        dragBoundFunc={dragBoundFunc}
        onClick={() => selectElement(element.id)}
        onTap={() => selectElement(element.id)}
        onDragMove={(e: Konva.KonvaEventObject<DragEvent>) => {
          const node = e.target;
          onDragMove?.(element.id, node.x(), node.y(), node.width(), node.height(), node);
        }}
        onDragEnd={handleDragEnd}
        onTransform={handleTransform}
        onTransformEnd={handleTransformEnd}
        onDblClick={onDoubleClick}
        onDblTap={onDoubleClick}
      >
        {element.contrastMode === 'bg-box' &&
          !isEditing &&
          (() => {
            const bgW = isResizing ? resizeDims.w : element.width;
            const bgH = isResizing ? resizeDims.h : expectedHeight;
            const bgOpacity = element.contrastIntensity / 100;
            const bgShape = element.boxShapeType ?? 'rectangle';
            const bgFill = element.contrastColor;

            const commonProps = {
              opacity: bgOpacity,
              fill: bgFill,
              listening: false,
              perfectDrawEnabled: false,
            };

            switch (bgShape) {
              case 'circle':
                return (
                  <Circle
                    x={bgW / 2}
                    y={bgH / 2}
                    radius={Math.min(bgW, bgH) / 2}
                    {...commonProps}
                  />
                );
              case 'ellipse':
                return (
                  <Ellipse
                    x={bgW / 2}
                    y={bgH / 2}
                    radiusX={bgW / 2}
                    radiusY={bgH / 2}
                    {...commonProps}
                  />
                );
              case 'star':
                return (
                  <Star
                    x={bgW / 2}
                    y={bgH / 2}
                    numPoints={5}
                    innerRadius={Math.min(bgW, bgH) * 0.2}
                    outerRadius={Math.min(bgW, bgH) / 2}
                    {...commonProps}
                  />
                );
              case 'triangle':
                return (
                  <RegularPolygon
                    x={bgW / 2}
                    y={bgH / 2}
                    sides={3}
                    radius={Math.min(bgW, bgH) / 2}
                    {...commonProps}
                  />
                );
              case 'rectangle':
              default:
                return (
                  <Rect
                    width={bgW}
                    height={bgH}
                    cornerRadius={(((element.boxRoundness ?? 0) / 100) * Math.min(bgW, bgH)) / 2}
                    {...commonProps}
                  />
                );
            }
          })()}

        {/* Transparent content-box Rect — always rendered so the Transformer has
            a child with reliable content-box dimensions. Without this, the
            Transformer computes its bounding box from the KonvaImage (which
            includes contrast padding) and handles drift after resize. During
            resize, uses in-progress dimensions so handles track the drag. */}
        {!isEditing && (
          <Rect
            width={isResizing ? resizeDims.w : element.width}
            height={isResizing ? resizeDims.h : expectedHeight}
            fill="transparent"
            listening
            perfectDrawEnabled={false}
          />
        )}

        {/* Render the rasterized rich text. listening MUST stay true: this is
            the only hit-detectable region of the Group. Setting listening=false
            here makes clicks on the visible text pass through to the Stage,
            which deselects (selection becomes impossible). */}
        {!isEditing &&
          renderedImage &&
          (() => {
            const cp = element.contrastPadding;
            const scaleFactor =
              renderedFontSizeRef.current > 0 ? element.fontSize / renderedFontSizeRef.current : 1;
            const imgH = (renderedImage.height / 2) * scaleFactor;
            // Compute vertical offset for middle/bottom alignment.
            // The element spans element.height starting at local y=0 (i.e.
            // absolute element.y). We place the rasterized text image at the
            // correct y within that band.
            //
            // Legacy/heightless layers (persisted before `height` was seeded
            // for text — see propagation.ts `seedGeometry`) have no
            // `element.height` at all. Rather than silently disable
            // middle/bottom for those rows, fall back to "the rest of the
            // stage below this layer's y" — the intuitive reading of
            // "bottom" as the bottom of the artboard. Read the Stage's own
            // size (divided by its own scale, since callers render at
            // display px scaled up/down from the authoring-pixel coordinate
            // space element.x/y/width/height already live in) rather than
            // assuming a fixed stage size, so this works for both the
            // Concept Workbench (scaled Stage) and the legacy Home-composer
            // media studio (unscaled Stage) that also render this component.
            let vAlignOffsetY = -cp.top * scaleFactor;
            if (element.verticalAlign && element.verticalAlign !== 'top') {
              const stage = shapeRef.current?.getStage();
              const nativeStageHeight = stage ? stage.height() / (stage.scaleY() || 1) : undefined;
              const bandHeight =
                element.height ||
                (nativeStageHeight !== undefined
                  ? Math.max(0, nativeStageHeight - element.y)
                  : undefined);
              if (bandHeight) {
                vAlignOffsetY =
                  element.verticalAlign === 'middle' ? (bandHeight - imgH) / 2 : bandHeight - imgH;
              }
            }
            return (
              <KonvaImage
                image={renderedImage}
                x={-cp.left * scaleFactor}
                y={vAlignOffsetY}
                width={(renderedImage.width / 2) * scaleFactor}
                height={imgH}
                perfectDrawEnabled={false}
              />
            );
          })()}
      </Group>

      {isSelected && !isEditing && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 30 || newBox.height < 10) {
              return oldBox;
            }
            return newBox;
          }}
          enabledAnchors={[
            'top-left',
            'top-center',
            'top-right',
            'middle-left',
            'middle-right',
            'bottom-left',
            'bottom-center',
            'bottom-right',
          ]}
          // Editorial monochrome chrome — no Konva-blue defaults.
          borderStroke="rgba(0,0,0,0.55)"
          borderStrokeWidth={1}
          borderDash={[4, 4]}
          anchorStroke="rgba(0,0,0,0.65)"
          anchorFill="#ffffff"
          anchorSize={9}
          anchorCornerRadius={4}
          rotateAnchorOffset={28}
          ignoreStroke
        />
      )}

      {isResizing && (
        <DimensionLabel x={element.x} y={element.y} width={resizeDims.w} height={resizeDims.h} />
      )}
    </>
  );
}
